import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { Input, PrimaryButton, Checkbox, Switch } from '@components/atoms';
import { getFetcher, SwButtonSection } from '../../../../App';
import { css } from '@emotion/react';
import { dialogContentsState } from '@components/templates';
import { FetchResponse } from '@models/common';
import he from 'he';
import {
  SettingMode,
  StrokeSetting,
  CommonSettingItem,
  ResultSettingItem,
  StrokeSettingInfo,
  elvoSettingItem,
  windowingSettingItem,
  reportBottomTextItem,
  SCSIchAddItem,
} from '@models/strokeSetting/setting';
import { useCatchLeave } from 'utils/hooks';
import SettingCommonMethods from '../../../../utils/settingCommon';
import _ from 'lodash';
import { REGEX_IP } from '@components/templates/Fields/MyAccount';
import { submitDebounce } from '../../../../utils/lib';
import { REGEX_KOREAN, REGEX_NUMBER } from '@variables/utils';
import {
  StyledStrokeSettingWrap,
  StyledSettingArea,
  StyledSettingBox,
  StyledInputList,
  StyledTxtLimited,
  StyledTransferList,
  StyledSmallTxt,
  StyledDuplicateDescription,
} from './style';
import useStaticDialog from 'utils/hooks/useStaticDialog';

type Settings = CommonSettingItem &
  ResultSettingItem &
  elvoSettingItem &
  windowingSettingItem &
  reportBottomTextItem &
  SCSIchAddItem;

interface transferProps {
  [key: string]: boolean;
}

const DISABLED_TARGETS = ['acquisitionDate', 'analysisStartDate', 'analysisEndDate'];

const StrokeSettingPage = () => {
  const { confirmTurnOnOffDialog } = useStaticDialog();
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const [mode, setMode] = useState<SettingMode>('CLASS3');
  const [currentMode, setCurrentMode] = useState<SettingMode>('CLASS3');
  const [backup, setBackup] = useState<Settings | null>(null);
  const [mutateSetting, setMutateSetting] = useState<Settings | null>(null);
  const [disableds, setDisableds] = useState<{ [key: string]: boolean }>({
    acquisitionTime: false,
    analysisStartTime: false,
    analysisEndTime: false,
  });
  const [settings, setSettings] = useState<{
    [key in SettingMode]: StrokeSettingInfo | null;
  }>({
    CLASS3: null,
    CLASS3_FDA: null,
    CLASS2: null,
  });

  const { errorHandler } = SettingCommonMethods();

  /**
   * Apply 활성화 상태값
   * true인걸 찾아서 key를 비교
   */
  const isMutate = useMemo(() => {
    if (currentMode !== mode) return false;

    // 데이터 값 동일 판단

    const dataCheck = _.isEqual(backup, mutateSetting);

    //  windowimg 빈값 저장 불가 > submit 버튼 비활성화
    if (mutateSetting?.windowLevel === '' || mutateSetting?.windowWidth === '') return true;

    if (!backup || !mutateSetting) return false;

    const backupData = Object.keys(backup)
      .filter((key) => !!backup[key as keyof typeof backup])
      .join('');
    const mutateSettingData = Object.keys(mutateSetting)
      .filter((key) => !!mutateSetting[key as keyof typeof mutateSetting])
      .join('');

    return backupData === mutateSettingData && dataCheck;
  }, [currentMode, mode, backup, mutateSetting]);

  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(!isMutate);

  //Default Data
  const DefaultDataList = [
    { value: 'Register' },
    { value: 'Patient ID' },
    { value: 'Name' },
    { value: 'Gender' },
    { value: 'Age' },
    { value: 'Status' },
  ];

  /**
   * 체크박스 핸들러
   * Date, Time 의 경우 상태 체이닝 설정 필요. acquisitionDate, importDate, analysisDate
   */
  const checkboxHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = ev.target.name as keyof typeof mutateSetting;
    const checked = ev.target.checked;

    if (mutateSetting && mutateSetting[name] === undefined) return;

    setMutateSetting((prevState) => {
      if (!prevState) return null;
      return { ...prevState, [name]: checked };
    });

    // disalbed 관리 대상의 경우 아래 로직 수행
    if (DISABLED_TARGETS.includes(name)) {
      // disabled 대상의 Date를 Time 으로 교체
      const disabledTarget = (name as string).split('Date')[0] + 'Time';

      // 해당 대상이 있는지 검사 후 상태에 맞게 변경
      if (disableds[disabledTarget] !== undefined) {
        setDisableds((prevState) => ({
          ...prevState,
          [disabledTarget]: checked === false,
        }));
      }
    }
  };

  /**
   *
   * @param ev
   * @returns
   * windowing 값 설정
   * 정수만 입력 가능 : wl은 마이너스 값 존재
   *
   */
  const windowingHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = ev.target.name as keyof typeof mutateSetting;
    let value = ev.target.value;

    const _hasDash = value.indexOf('-') === 0;
    value = value.replaceAll(REGEX_NUMBER, '');

    if (_hasDash) {
      if (name === 'windowLevel') {
        value = `-${value}`;
      } else {
        value = `${value}`;
      }
    }

    if (mutateSetting && mutateSetting[name] === undefined) return;

    setMutateSetting((prevState) => {
      if (!prevState) return null;
      return { ...prevState, [name]: value };
    });
  };

  /**
   *
   * @param ev
   * windowing - input text Handler
   * 최대/ 최소값 설정 필요
   * 빈값 저장 불가
   */
  const blurWindowingHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = ev.target.name as keyof typeof mutateSetting;
    let value = ev.target.value;

    const max = name === 'windowWidth' ? 4095 : name === 'windowLevel' ? 3071 : 9;
    const min = name === 'windowWidth' ? 1 : name === 'windowLevel' ? -1024 : 1;

    let _value = Number(value);

    if (_value === 0 || value === '') {
      console.log('?????');
      value = '';
    } else if (_value > max) {
      value = String(max);
    } else if (_value < min) {
      value = String(min);
    }

    setMutateSetting((prevState) => {
      if (!prevState) return null;
      return { ...prevState, [name]: value };
    });
  };

  /**
   *
   * @param ev
   * report Bottom Text 입력
   */
  const reportTxtHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    let value = ev.target.value.replace(REGEX_KOREAN, '');

    // 최대 100자까지 입력 가능
    if (value.length > 100) return;

    setMutateSetting((prevState) => {
      if (!prevState) return null;
      return { ...prevState, reportBottomText: value };
    });
  };

  /**
   * input text 입력시 변경 핸들러
   * elvosetting
   */
  const textBoxHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = ev.target.name as keyof typeof mutateSetting;
    let value = ev.target.value.replace(REGEX_IP, '');
    if (mutateSetting && mutateSetting[name] === undefined) return;
    // 입력시 표현 변경
    if (value === '.') value = '0.';

    // 소수점 최대 2자리 까지 노출
    let stringLength = ev.target.value.split('.');
    if (stringLength[1] && stringLength[1].length > 2) {
      value = `${stringLength[0]}.${stringLength[1].slice(0, 2)}`;
    }

    // 0 보다 작거나 1보다 클수 없음
    if (Number(value) < 0) value = '0';
    if (Number(value) > 1) value = '1';

    setMutateSetting((prevState) => {
      if (!prevState) return null;
      return { ...prevState, [name]: value };
    });
  };

  /**
   * input text blur 핸들러
   * elvosetting - 완전한 수치가 안나올 경우
   */
  const blurTextBoxHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = ev.target.name as keyof typeof mutateSetting;
    let value = Number(ev.target.value);

    setMutateSetting((prevState) => {
      if (!prevState) return null;
      return { ...prevState, [name]: Number(value) };
    });
  };

  /**
   * 모드 변경 핸들러
   * 모드 변경시 setting 값 재배치
   */
  const radioHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const mode = ev.target.value as SettingMode;
    setMode(mode);
    const next = settings[mode];

    if (!next) throw new Error('');
    initialSetting(next);
  };

  /**
   * Series config 변경 핸들러
   * 모드 변경시 setting 값 재배치
   */
  const seriesElvoHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    const name = ev.target.name;
    setMutateSetting((prevState) => {
      if (!prevState) return null;
      const cloneData = JSON.parse(JSON.stringify(prevState));
      cloneData[name] = value === 'true';
      return cloneData;
    });
  };

  /**
   * elvo 모드 변경 핸들러
   * 모드 변경시 setting 값 재배치
   */
  const radioElvoHandler = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const lvoModeValue = ev.target.value as SettingMode;
      const lvoModeName = ev.target.name;

      setMutateSetting((prevState) => {
        if (!prevState) return null;
        const cloneData = JSON.parse(JSON.stringify(prevState));
        cloneData.lvoThr = backup?.lvoThr;
        cloneData.dmsThr = backup?.dmsThr;
        cloneData.eicLmt = backup?.eicLmt;
        cloneData.eicThr = backup?.eicThr;
        cloneData[lvoModeName] = lvoModeValue;

        return cloneData;
      });
    },
    [backup],
  );

  /**
   * report 모드 변경 핸들러
   * 모드 변경시 setting 값 재배치
   */
  const optionsHandler = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const optionModeValue = ev.target.value as SettingMode;
      const optionModeName = ev.target.name;

      if (optionModeName === 'reportFormat') {
        setMutateSetting((prevState) => {
          if (!prevState) return null;
          const cloneData = JSON.parse(JSON.stringify(prevState));
          cloneData.reportFormat = backup?.reportFormat;
          cloneData[optionModeName] = optionModeValue;

          return cloneData;
        });
      } else {
        setMutateSetting((prevState) => {
          if (!prevState) return null;
          const cloneData = JSON.parse(JSON.stringify(prevState));
          cloneData.ichSegmentationRenderStyle = backup?.ichSegmentationRenderStyle;
          cloneData[optionModeName] = optionModeValue;

          return cloneData;
        });
      }
    },
    [backup],
  );

  /**
   * mode 이동 + 최초 데이터 fetch 시 데이터 세팅
   */
  const initialSetting = (data: StrokeSettingInfo) => {
    const { companyId, id, mode, ...settings } = data;
    const disabledTargets: { [key: string]: boolean } = {};

    for (let key in settings) {
      if (DISABLED_TARGETS.includes(key)) {
        const disabledTarget = (key as string).split('Date')[0] + 'Time';
        disabledTargets[disabledTarget] = settings[key as keyof typeof settings] === false;
      }
    }

    setDisableds((prevState) => {
      return {
        ...prevState,
        ...disabledTargets,
      };
    });

    settings.reportBottomText = he.decode(settings.reportBottomText || '');
    setMutateSetting(settings);
    setBackup(settings);
  };

  /**
   * Stroke Setting 정보 가져오기 (product - stroke)
   */
  const getStrokeSetting = async () => {
    const url = 'api/setting/scs/option';
    const accessToken = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      accessToken,
    })(url)
      .then((r) => {
        const { code, result } = r as FetchResponse<StrokeSetting>;
        if (code === '200') {
          const mode = result.companySettingMode;
          setMode(mode);
          setCurrentMode(mode);

          // const [CLASS3_FDA, CLASS3, CLASS2] = result.strokeSettingInfoList; //순서..?

          let CLASS3_FDA;
          let CLASS3;
          let CLASS2;

          result.strokeSettingInfoList.forEach((item) => {
            if (item.mode === 'CLASS3') {
              CLASS3 = item;
            } else if (item.mode === 'CLASS3_FDA') {
              CLASS3_FDA = item;
            } else if (item.mode === 'CLASS2') {
              CLASS2 = item;
            }
          });

          console.log('result.strokeSettingInfoList??', result.strokeSettingInfoList);
          console.log('CLASS3_FDA?', CLASS3_FDA, 'CLASS3?', CLASS3, 'CLASS2?', CLASS2);

          if (CLASS2 && CLASS3_FDA && CLASS3) {
            setSettings({
              CLASS3,
              CLASS3_FDA,
              CLASS2,
            });
            initialSetting(mode === 'CLASS3' ? CLASS3 : mode === 'CLASS2' ? CLASS2 : CLASS3_FDA);
          }
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   *
   */
  const updateStrokeSetting = async () => {
    const url = 'api/setting/scs/option';
    const accessToken = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const _tmpSetting = JSON.parse(JSON.stringify(mutateSetting));
    _tmpSetting.report = mode === 'CLASS3_FDA' ? false : _tmpSetting.report;
    const body = {
      ..._tmpSetting,
      id: settings[mode]!.id,
      mode,
    };

    const r = await getFetcher({
      method: 'POST',
      accessToken,
      body,
    })(url);

    const response = r as FetchResponse<any>;

    if (response.status === 'SUCCESS' && response.code === '200') {
      setBackup(mutateSetting);
      setCurrentMode(mode);
      setSettings((prevState) => {
        // 변경 데이터 적용
        // 독립적인 elvo setting에 의해 저장할 경우 다 바꿔야 함
        // 새로고침하면 적용된 데이터라 상관 없음
        const editData = {
          forcedAspects: mutateSetting?.forcedAspects,
          elvoSetting: mutateSetting?.elvoSetting,
          lvoThr: mutateSetting?.lvoThr,
          dmsThr: mutateSetting?.dmsThr,
          eicThr: mutateSetting?.eicThr,
          eicLmt: mutateSetting?.eicLmt,
        };

        const mergeDataCLASS3_FDA = {
          ...prevState['CLASS3_FDA'],
          ...editData,
        };
        const mergeDataCLASS3 = {
          ...prevState['CLASS3'],
          ...editData,
        };
        const mergeDataCLASS2 = {
          ...prevState['CLASS2'],
          ...editData,
        };

        return {
          ...prevState,
          ...{ ['CLASS3' as string]: mergeDataCLASS3 },
          ...{ ['CLASS3_FDA' as string]: mergeDataCLASS3_FDA },
          ...{ ['CLASS2' as string]: mergeDataCLASS2 },
        };
      });

      setDialogContents({
        type: 'plain',
        body: `It has been reflected.`,
        footer: (
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        ),
      });
    } else {
      errorHandler(r);
    }
  };

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */

  const setDataValue = (key: string, value: boolean) => {
    setMutateSetting((prevState) => {
      const _clone = JSON.parse(JSON.stringify(prevState));
      _clone[key] = value;
      return _clone;
    });
  };

  /**
   * 스위치 변경 컨펌
   * @param key
   * @param value
   */
  const confirmTurnOnOff = (key: string, value: boolean) => {
    const turn = value ? 'on' : 'off';

    //먼저 바꾸고 컨펌에 따라 결과 확정
    setMutateSetting((prevState) => {
      const _clone = JSON.parse(JSON.stringify(prevState));
      _clone[key] = value;
      return _clone;
    });

    confirmTurnOnOffDialog(
      turn,
      () => null,
      () => {
        setDataValue(key, !value);
      },
    );
  };

  /**
   * 최초 데이터 설정
   */
  useEffect(() => {
    (async () => {
      await getStrokeSetting();
    })();
  }, []);

  if (!mutateSetting) return null;

  return (
    <>
      <label>
        Setting<span className="sub-title">Select the required functions.</span>
      </label>
      <StyledStrokeSettingWrap>
        {/* Mode */}
        <StyledSettingArea>
          <strong>Mode</strong>
          <StyledSettingBox>
            <StyledInputList className="option-mode">
              <li>
                <Input type="radio" name="mode" value="CLASS3" checked={mode === 'CLASS3'} onChange={radioHandler}>
                  ClassⅢ - Default mode
                </Input>
              </li>
              <li>
                <Input
                  type="radio"
                  name="mode"
                  value="CLASS3_FDA"
                  checked={mode === 'CLASS3_FDA'}
                  onChange={radioHandler}
                >
                  ClassⅢ - FDA mode
                  <StyledSmallTxt>
                    (Setting to receive reports from the ‘Send to PACS’ menu does not send them to PACS)
                  </StyledSmallTxt>
                </Input>
              </li>
              <li>
                <Input type="radio" name="mode" value="CLASS2" checked={mode === 'CLASS2'} onChange={radioHandler}>
                  ClassⅡ
                </Input>
              </li>
            </StyledInputList>
          </StyledSettingBox>
        </StyledSettingArea>

        {/*Default Data*/}
        {mode !== 'CLASS2' && (
          <>
            <StyledSettingArea>
              <strong>Default Data</strong>
              <StyledSettingBox>
                <StyledInputList>
                  {DefaultDataList.map((list) => {
                    return (
                      <li key={list.value}>
                        <Checkbox disabled checked>
                          {list.value}
                        </Checkbox>
                      </li>
                    );
                  })}
                </StyledInputList>
              </StyledSettingBox>
            </StyledSettingArea>

            {/*common settings*/}
            <StyledSettingArea>
              <strong>Common Settings</strong>
              <StyledSettingBox>
                <StyledInputList>
                  <li>
                    <Checkbox name="acquisitionDate" checked={mutateSetting.acquisitionDate} onChange={checkboxHandler}>
                      Acquisition Date
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="acquisitionTime"
                      checked={mutateSetting.acquisitionTime}
                      disabled={disableds.acquisitionTime}
                      onChange={checkboxHandler}
                    >
                      Acquisition Time
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="sinceScan"
                      checked={mutateSetting.sinceScan}
                      disabled={disableds.sinceScan}
                      onChange={checkboxHandler}
                    >
                      Since Scan
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="analysisStartDate"
                      checked={mutateSetting.analysisStartDate}
                      onChange={checkboxHandler}
                    >
                      Analysis Start Date
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="analysisStartTime"
                      checked={mutateSetting.analysisStartTime}
                      disabled={disableds.analysisStartTime}
                      onChange={checkboxHandler}
                    >
                      Analysis Start Time
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox name="analysisEndDate" checked={mutateSetting.analysisEndDate} onChange={checkboxHandler}>
                      Analysis End Date
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="analysisEndTime"
                      checked={mutateSetting.analysisEndTime}
                      disabled={disableds.analysisEndTime}
                      onChange={checkboxHandler}
                    >
                      Analysis End Time
                    </Checkbox>
                  </li>
                  {mode === 'CLASS3' && (
                    <li>
                      <Checkbox name="report" checked={mutateSetting.report} onChange={checkboxHandler}>
                        Report
                      </Checkbox>
                    </li>
                  )}
                </StyledInputList>
              </StyledSettingBox>
            </StyledSettingArea>
          </>
        )}

        {/* ASPECTS setting */}
        <StyledSettingArea>
          <strong>ASPECTS setting</strong>
          <StyledSettingBox>
            <StyledInputList className="result-list">
              <li>
                <Checkbox name="forcedAspects" checked={mutateSetting.forcedAspects} onChange={checkboxHandler}>
                  ASPECTS analysis is performed regardless of ELVO results
                </Checkbox>
              </li>
            </StyledInputList>
          </StyledSettingBox>
        </StyledSettingArea>


        {/* ELVO setting */}
          <StyledSettingArea>
            <strong>ELVO setting</strong>
            <StyledSettingBox>
              <StyledInputList>
                <li>
                  <Input
                    type="radio"
                    name="elvoSetting"
                    value="DEFAULT"
                    checked={mutateSetting.elvoSetting === 'DEFAULT'}
                    onChange={radioElvoHandler}
                  >
                    Default
                  </Input>
                </li>
                <li>
                  <Input
                    type="radio"
                    name="elvoSetting"
                    value="CUSTOMIZE"
                    checked={mutateSetting.elvoSetting === 'CUSTOMIZE'}
                    onChange={radioElvoHandler}
                  >
                    Customize
                  </Input>
                </li>
              </StyledInputList>
              {mutateSetting.elvoSetting === 'CUSTOMIZE' && (
                <StyledInputList>
                  <li>
                    <Input
                      type="text"
                      name="lvoThr"
                      className={'lvo-setting'}
                      value={String(mutateSetting.lvoThr)}
                      onChange={textBoxHandler}
                      onBlur={blurTextBoxHandler}
                    >
                      LVO_Thr
                    </Input>
                  </li>
                  <li>
                    <Input
                      type="text"
                      name="dmsThr"
                      className={'lvo-setting'}
                      value={String(mutateSetting.dmsThr)}
                      onChange={textBoxHandler}
                      onBlur={blurTextBoxHandler}
                    >
                      DMS_Thr
                    </Input>
                  </li>
                  <li>
                    <Input
                      type="text"
                      name="eicThr"
                      className={'lvo-setting'}
                      value={String(mutateSetting.eicThr)}
                      onChange={textBoxHandler}
                      onBlur={blurTextBoxHandler}
                    >
                      EIC_Thr
                    </Input>
                  </li>
                  <li>
                    <Input
                      type="text"
                      name="eicLmt"
                      className={'lvo-setting'}
                      value={String(mutateSetting.eicLmt)}
                      onChange={textBoxHandler}
                      onBlur={blurTextBoxHandler}
                    >
                      EIC_lmt
                    </Input>
                  </li>
                </StyledInputList>
              )}
            </StyledSettingBox>
          </StyledSettingArea>

        {/* ASPECTS confidence level */}
        <StyledSettingArea>
          <strong>ASPECTS confidence level</strong>
          <StyledSettingBox>
            <StyledInputList>
              <li>
                <Input
                  type="text"
                  name="aspectsConfidenceLevel"
                  className={'windowing'}
                  value={String(mutateSetting.aspectsConfidenceLevel)}
                  onChange={windowingHandler}
                  onBlur={blurWindowingHandler}
                  // disabled={mode === 'CLASS2'}
                >
                  Default
                </Input>
                <StyledSmallTxt>Input range : 1~9 / Only integer</StyledSmallTxt>
              </li>
            </StyledInputList>
          </StyledSettingBox>
        </StyledSettingArea>

        {/* Windowing */}
        <StyledSettingArea>
          <strong>Windowing</strong>
          <StyledSettingBox>
            <StyledInputList>
              <li>
                <Input
                  type="text"
                  name="windowLevel"
                  className={'windowing'}
                  value={mutateSetting.windowLevel}
                  onChange={windowingHandler}
                  onBlur={blurWindowingHandler}
                >
                  WL
                </Input>
              </li>
              <li>
                <Input
                  type="text"
                  name="windowWidth"
                  className={'windowing'}
                  value={mutateSetting.windowWidth}
                  onChange={windowingHandler}
                  onBlur={blurWindowingHandler}
                >
                  WW
                </Input>
              </li>
            </StyledInputList>
          </StyledSettingBox>
        </StyledSettingArea>

        {mode === 'CLASS3' && (
          <>
            {/* Result */}
            <StyledSettingArea>
              <strong>Result</strong>
              <StyledSettingBox>
                <StyledInputList className="result-list">
                  <li>
                    <Checkbox
                      name="includingNotSuspected"
                      checked={mutateSetting.includingNotSuspected}
                      onChange={checkboxHandler}
                    >
                      Including not suspected
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="includingSuspectedHemisphere"
                      checked={mutateSetting.includingSuspectedHemisphere}
                      onChange={checkboxHandler}
                    >
                      Including suspected hemisphere
                    </Checkbox>
                  </li>
                  <li>
                    <Checkbox
                      name="includingHuLevel"
                      checked={mutateSetting.includingHuLevel}
                      onChange={checkboxHandler}
                    >
                      Including HU level
                    </Checkbox>
                  </li>
                </StyledInputList>
              </StyledSettingBox>
            </StyledSettingArea>

            {/* Report */}
            <StyledSettingArea>
              <strong>Report</strong>
              <StyledSettingBox>
                <StyledInputList>
                  <li>
                    <Input
                      type="radio"
                      name="reportFormat"
                      value="INDIVIDUAL"
                      checked={mutateSetting.reportFormat === 'INDIVIDUAL'}
                      onChange={optionsHandler}
                    >
                      Individual report
                    </Input>
                  </li>
                  <li>
                    <Input
                      type="radio"
                      name="reportFormat"
                      value="CONSOLIDATED"
                      checked={mutateSetting.reportFormat === 'CONSOLIDATED'}
                      onChange={optionsHandler}
                    >
                      Consolidated report
                    </Input>
                  </li>
                </StyledInputList>
              </StyledSettingBox>
            </StyledSettingArea>
          </>
        )}

        {/* ICH segmentation */}
        {(mode === 'CLASS3' || mode === 'CLASS2') && (
          <StyledSettingArea>
            <strong>ICH segmentation</strong>
            <StyledSettingBox>
              <StyledInputList>
                <li>
                  <Input
                    type="radio"
                    name="ichSegmentationRenderStyle"
                    value="LINE"
                    checked={mutateSetting.ichSegmentationRenderStyle === 'LINE'}
                    onChange={optionsHandler}
                  >
                    Line
                  </Input>
                </li>
                <li>
                  <Input
                    type="radio"
                    name="ichSegmentationRenderStyle"
                    value="FILL"
                    checked={mutateSetting.ichSegmentationRenderStyle === 'FILL'}
                    onChange={optionsHandler}
                  >
                    Fill
                  </Input>
                </li>
              </StyledInputList>
            </StyledSettingBox>
          </StyledSettingArea>
        )}

        {mode === 'CLASS3' && (
          <>
            {/* ICH probability report*/}
            <StyledSettingArea>
              <strong>ICH probability</strong>
              <StyledSettingBox>
                <StyledTransferList>
                  <li>
                    <span className={'send-off'}>Off</span>
                    <Switch
                      defaultValue={mutateSetting.ichProbability}
                      onChangeValue={(e) => setDataValue('ichProbability', e)}
                    />
                    <span>On</span>
                  </li>
                </StyledTransferList>
              </StyledSettingBox>
            </StyledSettingArea>

            {/* ELVO probability report*/}
            <StyledSettingArea>
              <strong>ELVO probability</strong>

              <StyledSettingBox>
                <StyledTransferList>
                  <li>
                    <span className={'send-off'}>Off</span>

                    <Switch
                      defaultValue={mutateSetting.elvoProbability}
                      onChangeValue={(e) => setDataValue('elvoProbability', e)}
                    />
                    <span>On</span>
                  </li>
                </StyledTransferList>
              </StyledSettingBox>
            </StyledSettingArea>
          </>
        )}

        {(mode === 'CLASS3' || mode === 'CLASS2') && (
          <>
            {/* Report bottom text*/}
            <StyledSettingArea>
              <strong>Report bottom text</strong>
              <StyledSettingBox>
                <StyledInputList>
                  <li>
                    <Input
                      type="text"
                      name="reportBottomText"
                      placeholder=""
                      className={'report_txt'}
                      value={mutateSetting.reportBottomText}
                      onChange={reportTxtHandler}
                    />
                    <StyledTxtLimited className="txt_limited">
                      <span className="now">
                        {mutateSetting.reportBottomText === null || mutateSetting.reportBottomText.length === 0
                          ? 0
                          : mutateSetting.reportBottomText.length > 100
                          ? 100
                          : mutateSetting.reportBottomText.length}
                      </span>
                      <span className="total">/100</span>
                    </StyledTxtLimited>
                    <p className="report_txt_help">
                      Only English, numbers, and some special characters can be entered.
                    </p>
                  </li>
                </StyledInputList>
              </StyledSettingBox>
            </StyledSettingArea>

            {/* Transfer */}
            <StyledSettingArea>
              <strong>Transfer</strong>
              <StyledSettingBox>
                <StyledTransferList>
                  <li>
                    <span className={'send-off'}>Off</span>

                    <Switch
                      defaultValue={mutateSetting.transfer}
                      onChangeValue={(e) => confirmTurnOnOff('transfer', e)}
                    />
                    <span>On</span>
                  </li>
                </StyledTransferList>
              </StyledSettingBox>
            </StyledSettingArea>
          </>
        )}


      {/*  /!* Duplicate check *!/*/}
        <StyledSettingArea>
          <strong>Duplicate check</strong>
          <StyledSettingBox>
            <StyledTransferList>
              <li>
                <span className={'send-off'}>Off</span>

                <Switch
                  defaultValue={mutateSetting.checkDuplicate}
                  onChangeValue={(e) => confirmTurnOnOff('checkDuplicate', e)}
                />
                <span>On</span>
              </li>
            </StyledTransferList>
            <StyledDuplicateDescription>
              If the duplicate check is off, you can analyze the same data repeatedly.
            </StyledDuplicateDescription>
          </StyledSettingBox>
        </StyledSettingArea>
      </StyledStrokeSettingWrap>

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(updateStrokeSetting)}
          disabled={isMutate}
        >
          Apply
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default StrokeSettingPage;
